import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useEffect, useState } from 'react';
import SEO from '@/components/SEO';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground leading-relaxed transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const MaximCard = ({ number, title, description, authorities, legalMaxim, delay = 0 }: { 
  number: number; 
  title: string; 
  description: string; 
  authorities?: string;
  legalMaxim?: string;
  delay?: number; 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`p-6 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20 transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0 w-12 h-12 bg-gold text-white rounded-full flex items-center justify-center font-bold text-lg">
          {number}
        </div>
        <div className="flex-1">
          <h3 className="font-rockwell text-xl font-semibold text-primary mb-3">
            {title}
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            {description}
          </p>
          {authorities && (
            <>
              <p className="text-sm text-muted-foreground mb-2 mt-4">
                <span className="font-semibold text-primary">Authorities:</span> {authorities}
              </p>
              {legalMaxim && (
                <p className="text-sm text-muted-foreground italic">
                  <span className="font-semibold text-primary not-italic">Legal maxim:</span> {legalMaxim}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const MaximsPage = () => {
  const maxims = [
    {
      title: "A workman is worthy of his hire",
      description: "Every person who provides labor, services, or goods deserves fair compensation for their work and effort.",
      authorities: "Exodus 20:15; Leviticus 19:13; Matthew 10:10; Luke 10:7; II Timothy 2:6",
      legalMaxim: "It is against equity for free men not to have the free disposal of their own property."
    },
    {
      title: "All are equal under the law",
      description: "Every individual, regardless of status or position, is subject to and protected by the same legal principles and standards.",
      authorities: "Exodus 21:23 - 25; Leviticus 24:17 -21; Deuteronomy 1:17, 19-21 Matthew 22:36-40; Luke 10:17 Colossians 3:25",
      legalMaxim: "No one is above the law. Commerce, by the law of nations, ought to be common and not to be converted into a monopoly and the private gain of a few."
    },
    {
      title: "In commerce, truth is sovereign",
      description: "Truth is the highest authority in all commercial dealings and transactions, superseding all other considerations.",
      authorities: "Exodus 20:16 Psalms 117:2; John 8:32; II Corinthians 13:8",
      legalMaxim: "\"To lie is to go against the mind\" Oriental Proverb: \"Of all that is good Sublimity is supreme.\""
    },
    {
      title: "Truth is expressed in the form of an affidavit",
      description: "The proper legal method to present truth in commerce is through a sworn statement of facts under penalty of perjury.",
      authorities: "Leviticus 5:4-5; Leviticus 6:3-5; Leviticus 19:11-13; Numbers 3:2; Matthew 5:33; James 5:12",
      legalMaxim: "The proper legal method to present truth in commerce is through a sworn statement of facts under penalty of perjury."
    },
    {
      title: "An unrebutted affidavit stands as truth in commerce",
      description: "When an affidavit is presented and no factual rebuttal is provided, it is accepted as the established truth.",
      authorities: "Hebrews 4:16; Philippians 4:5; Ephesians 6:19-21",
      legalMaxim: "He who fails to assert his rights has none."
    },
    {
      title: "An unrebutted affidavit becomes the judgment in commerce",
      description: "An affidavit that remains unchallenged transforms into a binding legal determination with the force of law.",
      authorities: "1 Peter 1:25; Hebrews 6:13-15",
      legalMaxim: "He who does not deny, admits."
    },
    {
      title: "A matter must be expressed to be resolved",
      description: "Issues in commerce cannot be addressed or settled unless they are clearly articulated and brought forward.",
      authorities: "Hebrews 6:16-17",
      legalMaxim: "Any proceeding in court, tribunal, or arbitration form consists of a contest or \"duel\" of commercial affidavits wherein the points remaining unrebuted in the end stand as the truth and the matters to which the judgment of the law is applied."
    },
    {
      title: "He who leaves the battlefield first loses by default",
      description: "In commercial disputes, the party who abandons their position or fails to respond forfeits their claim by default.",
      authorities: "Book of Job; Matthew 10:22",
      legalMaxim: "He who does not repel a wrong when he can occasions it, must not have suffered any wrong doing."
    },
    {
      title: "Sacrifice is the measure of credibility (nothing ventured, nothing gained)",
      description: "True commitment and credibility in commerce are demonstrated through the willingness to risk something of value.",
      authorities: "Acts 7; One who is not damaged, put at risk, or willing to swear an oath on his commercial liability for the truth of his statements. And legitimacy of his actions has no bias to assert claims or charges, and forfeits all credibility and right to claim authority.",
      legalMaxim: "You who bears the burden are also to derive the benefit."
    },
    {
      title: "A lien or claim, properly filed and unrebutted, stands as law in commerce",
      description: "When a lien or claim is correctly documented and remains unchallenged, it becomes enforceable as commercial law.",
      authorities: "Genesis 2-3; Matthew 4; Book of Revelation",
      legalMaxim: "If the plaintiff does not prove his case, the defendant is absolved. 10.1. A rebuttal affidavit of truth supported by evidence point by point. 10.2. Payment. 10.3. Agreement. 10.4. Resolution by a jury according to the rules of common law."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <SEO
        title="Maxims of Commercial Law | Freeman & Crown Law"
        description="Learn the 10 fundamental maxims of commercial law that govern truth, fairness, and accountability in all commercial transactions. Natural law principles explained."
        image="/lovable-uploads/d54b2ac6-69a1-4d86-ac6e-e08d24d13ec9.png"
        url={window.location.origin + '/maxims'}
        type="website"
      />
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-8">
              Maxims of Commercial Law
            </h1>
            <div className="max-w-3xl mx-auto lg:max-w-none lg:mx-8 space-y-6">
              <AnimatedParagraph delay={200}>
                The Maxims in Commercial Law are fundamental principles that govern truth, fairness, and accountability in all commercial transactions. 
                These time-honored principles ensure that commerce operates on the foundation of honesty, equity, and natural justice.
              </AnimatedParagraph>
              <AnimatedParagraph delay={400}>
                Rooted in natural law and universal principles of fair dealing, these maxims serve as the cornerstone 
                for all lawful commercial activity and the pursuit of truth in business relationships.
              </AnimatedParagraph>
              
              <blockquote className="italic text-foreground/90 max-w-3xl mx-auto mb-6 text-center">
                "Equity is that idea of justice which contravenes the written law." <span className="not-italic font-medium">— Aristotle</span>
              </blockquote>
            </div>
          </div>

          {/* The 10 Maxims */}
          <div className="space-y-8">
            <h2 className="font-rockwell text-3xl font-semibold text-primary text-center mb-12">
              The 10 Maxims in Commercial Law
            </h2>
            
            <div className="grid gap-8">
              {maxims.map((maxim, index) => (
                <MaximCard
                  key={index}
                  number={index + 1}
                  title={maxim.title}
                  description={maxim.description}
                  authorities={maxim.authorities}
                  legalMaxim={maxim.legalMaxim}
                  delay={600 + (index * 100)}
                />
              ))}
            </div>
          </div>

          {/* Closing Statement */}
          <div className="mt-16 p-8 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20">
            <h3 className="font-rockwell text-2xl font-semibold text-primary mb-6 text-center">
              Foundation of Natural Justice
            </h3>
            <div className="max-w-3xl mx-auto space-y-4">
              <AnimatedParagraph delay={1800}>
                These maxims represent more than legal principles—they embody the eternal quest for justice that transcends 
                written law. They remind us that true equity flows from conscience, fairness, and the natural order of right and wrong.
              </AnimatedParagraph>
              <AnimatedParagraph delay={2000}>
                In our work at Freeman & Crown Equity Law Group, these maxims guide every decision, every process, and every 
                remedy we pursue in service of natural rights and divine justice.
              </AnimatedParagraph>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default MaximsPage;